import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { useCallback, useState } from 'react'
import { Button } from '@app/components/core/button/Button'
import { useTranslations } from '@i18n/useLanguage'
import {
  ImageEditRow,
  ImagePreview,
} from '@app/components/blocks/imageEditRow/ImageEditRow'
import { EditButton } from '@app/components/core/button/EditButton'
import { PreviewImage } from '@app/components/core/image/PreviewImage'
import { useToast } from '@app/util/hooks/useToast'
import { Text } from '@app/components/core/typography/Text'
import { InfoAlert } from '@app/components/core/alert/InfoAlert'

type ExistingImage = { id: number; src: string }

export type ImageEditPanelProps = {
  title: string
  images: ExistingImage[]
  onSave: (deleted: number[], added: File[]) => Promise<boolean>
}

export const ImageEditPanel = ({
  title,
  images,
  onSave,
}: ImageEditPanelProps) => {
  const translations = useTranslations().app.common
  const profileTranslations = useTranslations().app.dashboard.profile
  const [editMode, setEditMode] = useState(false)
  const [newImages, setNewImages] = useState<ImagePreview[]>([])
  const [removedImages, setRemovedImages] = useState<number[]>([])
  const { showToast } = useToast()

  const save = useCallback(() => {
    // TODO: loader
    onSave(
      removedImages,
      newImages.map((x) => x.file),
    )
      .then((success) => {
        if (success) {
          // TODO: when based fixes file uploads enable error toasts
          // showToast(
          //   'success',
          //   profileTranslations.profileChangedSavedToast,
          //   1.5,
          // )
        } else {
          return
          // TODO: when based fixes file uploads enable error toasts
          //showToast('error', translations.genericToastFailureMessage)
        }
      })
      .catch(() => {
        return
        // TODO: when based fixes file uploads enable error toasts
        //showToast('error', translations.genericToastFailureMessage)
      })
    showToast('success', profileTranslations.profileChangedSavedToast, 1.5)
    newImages.forEach((image) => URL.revokeObjectURL(image.preview))
    setNewImages([])
    setRemovedImages([])
    setEditMode(false)
  }, [
    newImages,
    onSave,
    profileTranslations.profileChangedSavedToast,
    removedImages,
    showToast,
  ])

  const cancel = useCallback(() => {
    setNewImages([])
    setRemovedImages([])
    setEditMode(false)
  }, [])

  return (
    <Column
      alignItems="stretch"
      style={{
        backgroundColor: 'var(--palette-white)',
        padding: '1.5rem',
        borderRadius: '1.25rem',
      }}
    >
      <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Title.H4 style={{ marginBottom: '0' }}>{title}</Title.H4>
        <EditButton isVisible={!editMode} onClick={() => setEditMode(true)} />
      </Row>
      <Text.Medium color="var(--palette-blue-500)">
        {profileTranslations.imageUploadHint}
      </Text.Medium>
      {!editMode && (
        <Row style={{ flexWrap: 'wrap' }}>
          {images.map((image, i) => (
            <PreviewImage key={`existing_${i}`} src={image.src} />
          ))}
        </Row>
      )}
      {editMode && (
        <Column>
          <InfoAlert message={profileTranslations.imageUploadAlert} />
          <ImageEditRow
            existingImages={images}
            newImages={newImages}
            removedImages={removedImages}
            setNewImages={setNewImages}
            setRemovedImages={setRemovedImages}
          />
        </Column>
      )}
      {editMode && (
        <Row style={{ justifyContent: 'flex-end' }}>
          <Button
            variant="secondary"
            label={translations.cancel}
            onClick={cancel}
          />
          <Button variant="primary" label={translations.save} onClick={save} />
        </Row>
      )}
    </Column>
  )
}
